import { Injectable, signal } from '@angular/core';
import { fromEvent, merge, Subject, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private readonly INACTIVITY_TIMEOUT = this.getInactivityTimeout();

  private getInactivityTimeout(): number {
    const hostname = window.location.hostname;
    const isNonProd =
      hostname.includes('rct.solutionbrisdeglace.fr') ||
      hostname.includes('pprod.solutionbrisdeglace.fr') ||
      hostname === 'localhost';
    return isNonProd ? 5 * 60 * 1000 : 20 * 60 * 1000;
  }
  private readonly SESSION_EXPIRED_KEY = 'session_expired';
  private inactivityTimer$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  sessionExpired = signal<boolean>(localStorage.getItem(this.SESSION_EXPIRED_KEY) === 'true');

  startWatching(): void {
    if (this.sessionExpired()) {
      return;
    }

    localStorage.setItem(this.SESSION_EXPIRED_KEY, 'false');

    // Liste des événements à surveiller pour détecter l'activité
    const events = [
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'wheel'),
    ];

    // Fusionne tous les événements et les débounce pour éviter trop d'appels
    merge(...events)
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetTimer();
      });

    this.resetTimer();
  }

  stopWatching(): void {
    this.destroy$.next();
    this.inactivityTimer$.next();
  }

  private resetTimer(): void {
    this.inactivityTimer$.next();

    timer(this.INACTIVITY_TIMEOUT)
      .pipe(takeUntil(this.inactivityTimer$), takeUntil(this.destroy$))
      .subscribe(() => {
        this.sessionExpired.set(true);
        localStorage.setItem(this.SESSION_EXPIRED_KEY, 'true');
      });
  }

  reset(): void {
    this.sessionExpired.set(false);
    localStorage.removeItem(this.SESSION_EXPIRED_KEY);
  }
}
